import { Injectable } from '@angular/core';

/** Android FCM channel ID used for KilnLink alerts. Must match channel created in the app. */
export const ANDROID_PUSH_CHANNEL_ID = 'kilnlink_alerts';

declare const window: Window & {
  cordova?: {
    plugins?: {
      notification?: { local?: CordovaLocalNotificationPlugin };
      firebase?: {
        messaging?: {
          getToken: () => Promise<string>;
          requestPermission?: () => Promise<void>;
          onTokenRefresh?: (callback: (token: string) => void) => void;
          onMessage?: (callback: (data: unknown) => void) => void;
          onBackgroundMessage?: (callback: (data: unknown) => void) => void;
          listChannels?: () => Promise<unknown>;
        };
      };
    };
  };
};

/** Cordova Local Notifications plugin (katzer) - createChannel is Android-only. */
interface CordovaLocalNotificationPlugin {
  createChannel?: (options: Record<string, unknown>, callback?: () => void, scope?: unknown) => void;
}

export interface PushRegistrationResult {
  token: string;
  platform: string;
}

/**
 * Manages push notification registration and token lifecycle.
 * When running in Cordova with a push plugin (e.g. phonegap-plugin-push) installed,
 * registers for push and exposes the token. Otherwise no-ops for browser/builds without the plugin.
 *
 * Device permissions: The OS shows the permission prompt when we first call
 * requestPermissionAndRegister() (e.g. when the user enables "Push (this device)" in alert settings).
 * You do not need to call this at app startup just to get permissions.
 *
 * Optional startup: Call registerAtStartupIfAvailable() after login or in app init if you want to
 * re-establish the cached token after app restart and handle token refresh (FCM/APNs can issue new tokens).
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private cachedToken: string | null = null;
  private currentDeviceName: string | null = null;
  private registrationPromise: Promise<PushRegistrationResult | null> | null = null;

  /** True when running inside Cordova with Firebase Messaging available. */
  get isPushAvailable(): boolean {    
    return (
      typeof window !== 'undefined' &&
      !!window.cordova &&
      !!window.cordova.plugins &&
      !!window.cordova.plugins.firebase &&
      !!window.cordova.plugins.firebase.messaging
    );
  }

  private getFirebaseMessaging() {
    return window.cordova?.plugins?.firebase?.messaging;
  }

  /**
   * Request permission and register for push notifications.
   * Resolves with token and platform when successful, or null when unavailable/denied/error.
   */
  async requestPermissionAndRegister(_senderId?: string): Promise<PushRegistrationResult | null> {
    if (this.registrationPromise != null) {
      return this.registrationPromise;
    }
    if (!this.isPushAvailable) {
      return Promise.resolve(null);
    }
    this.registrationPromise = new Promise<PushRegistrationResult | null>((resolve) => {
      try {
        const messaging = this.getFirebaseMessaging();
        if (!messaging) {
          resolve(null);
          return;
        }

        console.log('Registering for push notifications via Firebase Messaging.');
        this.ensureAndroidChannelExists();

        const doGetToken = () =>
          messaging
            .getToken()
            .then((token: string) => {
              if (token) {
                console.log(`Received FCM token: ${token}`);
                this.cachedToken = token;
                resolve({ token, platform: this.getPlatform() });
              } else {
                resolve(null);
              }
            })
            .catch((err: unknown) => {
              console.error('Error getting FCM token', err);
              resolve(null);
            });

        if (messaging.requestPermission) {
          messaging
            .requestPermission()
            .then(() => doGetToken())
            .catch((err: unknown) => {
              console.error('FCM permission denied', err);
              resolve(null);
            });
        } else {
          doGetToken();
        }

        if (messaging.onTokenRefresh) {
          messaging.onTokenRefresh((token: string) => {
            console.log(`FCM token refreshed: ${token}`);
            this.cachedToken = token;
          });
        }
      } catch {
        resolve(null);
      }
    });
    return this.registrationPromise;
  }

  /**
   * Optional: call at app startup (e.g. after login or in root component init) to register for push
   * and restore the cached token. Use this if you want the token available without the user re-opening
   * alert settings, or to refresh the token. Does not force a permission prompt if already granted.
   */
  async registerAtStartupIfAvailable(senderId?: string): Promise<PushRegistrationResult | null> {
    return this.requestPermissionAndRegister(senderId);    
  }

  /** Returns the last known push token, or null. */
  getStoredToken(): string | null {
    return this.cachedToken;
  }

  /** Device name set when user registers from User Management (e.g. "Molly's iPhone"). */
  getCurrentDeviceName(): string | null {
    return this.currentDeviceName;
  }

  setCurrentDeviceName(name: string | null): void {
    this.currentDeviceName = name;
  }

  /** Clears cached token and device name (e.g. after logout). */
  clearStoredToken(): void {
    this.cachedToken = null;
    this.currentDeviceName = null;
    this.registrationPromise = null;
  }

  private getPlatform(): string {
    if (typeof window === 'undefined' || !window.cordova) return 'unknown';
    const device = (window as unknown as { device?: { platform?: string } }).device;
    return device?.platform || 'unknown';
  }

  /**
   * Ensure the Android FCM notification channel exists. Call at app startup so the
   * channel is created even before the user registers for push. No-op on non-Android.
   */
  ensureAndroidChannelExists(): void {
    this.ensureAndroidChannelCreated();
  }

  /**
   * Create the Android notification channel for FCM (required on Android 8.0+).
   * Uses cordova-plugin-local-notification's createChannel so server-sent FCM
   * messages with channelId 'kilnlink_alerts' display with high priority.
   * No-op if not Android or plugin not available.
   */
  private ensureAndroidChannelCreated(): void {
    if (this.getPlatform() !== 'Android') return;
    const cordova = (window as unknown as { cordova?: { plugins?: { notification?: { local?: CordovaLocalNotificationPlugin } } } }).cordova;
    const local = cordova?.plugins?.notification?.local;
    if (!local?.createChannel) return;
    try {
      local.createChannel(
        {
          androidChannelId: ANDROID_PUSH_CHANNEL_ID,
          androidChannelName: 'KilnLink Alerts',
          androidChannelDescription: 'KilnLink alerts and notifications',
          androidChannelImportance: 4, // NotificationManager.IMPORTANCE_HIGH
          androidChannelEnableVibration: true,
        } as unknown as Record<string, unknown>,
        () => console.log('PushNotificationService: Android channel created'),
        undefined
      );
    } catch (e) {
      console.warn('PushNotificationService: could not create Android channel', e);
    }
  }
}
